import React, { useState, useMemo } from 'react';
import { Check, Buildings, House, CaretUpDown, Plus, SpinnerGap, Door, Package } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Checkbox } from '@/components/ui/checkbox';
import { cn } from '@/lib/utils';
import { toast } from 'react-toastify';
import { AssetCategory, AssetType, AssetScope } from 'shared/types/asset';
import { useCreateAssetType, useCreateAssetCategory } from 'hooks/useAssets';

interface CategoryTypeSelectorProps {
  categories: AssetCategory[];
  selectedCategory: AssetCategory | null;
  selectedType: AssetType | null;
  onSelectCategory: (category: AssetCategory) => void;
  onSelectType: (type: AssetType) => void;
  /** Called after a new type is created so the parent can refetch categories */
  onTypeCreated?: () => void;
  /** Called after a new category is created so the parent can refetch and select it */
  onCategoryCreated?: (category: AssetCategory) => void;
}

const scopeLabel: Record<AssetScope, string> = {
  [AssetScope.BUILDING]: 'Gebouw',
  [AssetScope.UNIT]: 'Unit',
  [AssetScope.ROOM]: 'Ruimte',
  [AssetScope.PORTABLE]: 'Draagbaar',
};

const scopeIcon: Record<AssetScope, React.ReactNode> = {
  [AssetScope.BUILDING]: <Buildings className="h-3 w-3" />,
  [AssetScope.UNIT]: <House className="h-3 w-3" />,
  [AssetScope.ROOM]: <Door className="h-3 w-3" />,
  [AssetScope.PORTABLE]: <Package className="h-3 w-3" />,
};

const CategoryTypeSelector: React.FC<CategoryTypeSelectorProps> = ({
  categories,
  selectedCategory,
  selectedType,
  onSelectCategory,
  onSelectType,
  onTypeCreated,
  onCategoryCreated,
}) => {
  const [categoryOpen, setCategoryOpen] = useState(false);
  const [typeOpen, setTypeOpen] = useState(false);
  const [typeSearch, setTypeSearch] = useState('');
  const [categorySearch, setCategorySearch] = useState('');
  const [newTypeScopes, setNewTypeScopes] = useState<AssetScope[]>([]);

  const toggleNewTypeScope = (scope: AssetScope) => {
    setNewTypeScopes((prev) =>
      prev.includes(scope)
        ? prev.filter((s) => s !== scope)
        : [...prev, scope]
    );
  };

  const { createAssetType, isCreating: isCreatingType } = useCreateAssetType();
  const { createCategory, isCreating: isCreatingCategory } = useCreateAssetCategory();

  const activeCategories = useMemo(
    () => categories.filter((c) => c.active),
    [categories]
  );

  const availableTypes = useMemo(() => {
    if (!selectedCategory) return [];
    return selectedCategory.assetTypes?.filter((t) => t.active) || [];
  }, [selectedCategory]);

  const handleCreateTypeInline = async (typeName: string) => {
    if (!selectedCategory) return;

    try {
      const newType = await createAssetType({
        assetCategoryId: selectedCategory.id,
        name: typeName,
        scopes: newTypeScopes,
      });

      toast.success(`Type "${typeName}" aangemaakt`);
      setTypeOpen(false);
      setTypeSearch('');
      onTypeCreated?.();
      onSelectType(newType);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Fout bij aanmaken van type';
      toast.error(errorMessage);
      console.error('Error creating asset type inline:', err);
    }
  };

  const handleCreateCategoryInline = async (categoryName: string) => {
    try {
      const newCategory = await createCategory({ name: categoryName });

      toast.success(`Categorie "${categoryName}" aangemaakt`);
      setCategoryOpen(false);
      setCategorySearch('');
      onCategoryCreated?.(newCategory);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Fout bij aanmaken van categorie';
      toast.error(errorMessage);
      console.error('Error creating asset category inline:', err);
    }
  };

  const renderScopeBadges = (scopes: AssetScope[]) => {
    if (!scopes || scopes.length === 0) return null;
    return (
      <div className="flex items-center gap-1">
        {scopes.map((s) => (
          <Badge key={s} variant="outline" className="text-xs gap-1 px-1.5 py-0">
            {scopeIcon[s]}
            {scopeLabel[s]}
          </Badge>
        ))}
      </div>
    );
  };

  return (
    <div className="flex items-center gap-3 flex-wrap">
      {/* Category combobox */}
      <Popover open={categoryOpen} onOpenChange={setCategoryOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            className="w-[240px] justify-between font-normal"
          >
            {selectedCategory ? (
              <span>{selectedCategory.name}</span>
            ) : (
              <span className="text-muted-foreground">Selecteer categorie...</span>
            )}
            <CaretUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[240px] p-0" align="start">
          <Command>
            <CommandInput
              placeholder="Zoek of maak categorie..."
              value={categorySearch}
              onValueChange={setCategorySearch}
            />
            <CommandList>
              <CommandEmpty>
                <div className="flex flex-col items-center gap-2 py-2">
                  <span className="text-sm text-muted-foreground">Geen categorieën gevonden</span>
                  {categorySearch.trim() && (
                    <Button
                      variant="outline"
                      size="sm"
                      className="gap-1"
                      disabled={isCreatingCategory}
                      onClick={() => handleCreateCategoryInline(categorySearch.trim())}
                    >
                      {isCreatingCategory ? (
                        <SpinnerGap className="h-3 w-3 animate-spin" />
                      ) : (
                        <Plus className="h-3 w-3" />
                      )}
                      &quot;{categorySearch.trim()}&quot; aanmaken
                    </Button>
                  )}
                </div>
              </CommandEmpty>
              <CommandGroup>
                {activeCategories.map((category) => (
                  <CommandItem
                    key={category.id}
                    value={category.name}
                    onSelect={() => {
                      onSelectCategory(category);
                      setCategoryOpen(false);
                      setCategorySearch('');
                    }}
                  >
                    <Check
                      className={cn(
                        'mr-2 h-4 w-4',
                        selectedCategory?.id === category.id ? 'opacity-100' : 'opacity-0'
                      )}
                    />
                    <span>{category.name}</span>
                    <Badge variant="secondary" className="ml-auto text-xs">
                      {(category.assetTypes?.filter((t) => t.active) || []).length}
                    </Badge>
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>

      {/* Type combobox */}
      <Popover open={typeOpen} onOpenChange={(open) => {
        setTypeOpen(open);
        if (open) setNewTypeScopes([AssetScope.UNIT]);
      }}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            className="w-[280px] justify-between font-normal"
            disabled={!selectedCategory}
          >
            {selectedType ? (
              <div className="flex items-center gap-2">
                <span>{selectedType.name}</span>
              </div>
            ) : (
              <span className="text-muted-foreground">
                {!selectedCategory
                  ? 'Selecteer eerst een categorie'
                  : availableTypes.length === 0
                    ? 'Geen types — klik om toe te voegen'
                    : 'Selecteer type...'}
              </span>
            )}
            <CaretUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[280px] p-0" align="start">
          <Command>
            <CommandInput
              placeholder="Zoek of maak type..."
              value={typeSearch}
              onValueChange={setTypeSearch}
            />
            <CommandList>
              <CommandEmpty>
                <div className="flex flex-col items-center gap-2 py-2">
                  <span className="text-sm text-muted-foreground">Geen types gevonden</span>
                  {typeSearch.trim() && (
                    <>
                      {/* Scope picker */}
                      <div className="w-full px-3">
                        <p className="text-xs text-muted-foreground mb-1.5">Scope:</p>
                        <div className="flex flex-wrap gap-x-3 gap-y-1">
                          {Object.values(AssetScope).map((s) => (
                            <label key={s} className="flex items-center gap-1.5 cursor-pointer">
                              <Checkbox
                                checked={newTypeScopes.includes(s)}
                                onCheckedChange={() => toggleNewTypeScope(s)}
                                className="h-3.5 w-3.5"
                              />
                              <span className="text-xs flex items-center gap-1">
                                {scopeIcon[s]}
                                {scopeLabel[s]}
                              </span>
                            </label>
                          ))}
                        </div>
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        className="gap-1"
                        disabled={isCreatingType || newTypeScopes.length === 0}
                        onClick={() => handleCreateTypeInline(typeSearch.trim())}
                      >
                        {isCreatingType ? (
                          <SpinnerGap className="h-3 w-3 animate-spin" />
                        ) : (
                          <Plus className="h-3 w-3" />
                        )}
                        &quot;{typeSearch.trim()}&quot; aanmaken
                      </Button>
                    </>
                  )}
                </div>
              </CommandEmpty>
              <CommandGroup>
                {availableTypes.map((type) => (
                  <CommandItem
                    key={type.id}
                    value={type.name}
                    onSelect={() => {
                      onSelectType(type);
                      setTypeOpen(false);
                      setTypeSearch('');
                    }}
                  >
                    <Check
                      className={cn(
                        'mr-2 h-4 w-4',
                        selectedType?.id === type.id ? 'opacity-100' : 'opacity-0'
                      )}
                    />
                    <span className="flex-1 truncate">{type.name}</span>
                    <div className="ml-auto flex items-center gap-1">
                      {type.scopes?.map((s) => (
                        <Badge key={s} variant="outline" className="text-xs gap-0.5 px-1 py-0">
                          {scopeIcon[s]}
                        </Badge>
                      ))}
                    </div>
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>

      {/* Scope badges */}
      {selectedType && renderScopeBadges(selectedType.scopes)}
    </div>
  );
};

export default CategoryTypeSelector;
